import React from "react";
import { useHookFormMask } from "use-mask-input";
import { SimpleFieldProps } from "../types";
import classNames from "classnames";
import FormField from "../FormField";
import useDefaultValueAs from "@/hooks/useDefaultValueAs";
import { inputIcons } from "./iconMap";
import "./styles.scss";

const protectedFields = ["emailAddress", "emailAddressValidation"];

export const TextInput = ({
  t,
  field,
  register,
  touchedFields,
  errors,
  className: classNameProp,
  values,
  setValue,
  dynamicValidation,
  nextStepTriggerCount,
}: SimpleFieldProps) => {
  const dataQA = `${field.type}-${field.name}`;
  const inputID = field.id || field.name;
  const className = classNames(`input`, {
    [field.class as string]: typeof field.class === "string" && field.class.length > 0,
    [classNameProp as string]: classNameProp,
    "has-icon": field.rightIcon,
  });

  const baseProps = {
    id: inputID,
    "data-qa": dataQA,
    "aria-label": t[field.name],
    className: "input__field",
  };

  const Tag = field.component === "TextArea" ? "textarea" : "input";
  const hasNotEmptyValue = values?.[field.name] && values?.[field.name].length > 0;
  const shouldShowError =
    Boolean(errors[field.name]) &&
    (nextStepTriggerCount === undefined ||
      Boolean(touchedFields?.[field.name]) ||
      nextStepTriggerCount > 0);
  const validate = !shouldShowError || !errors[field.name];
  const validField =
    (touchedFields?.[field?.name] && !errors[field.name] && hasNotEmptyValue) || false;

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

  const validateEmail = {
    maxLength: 33,
    required: "Email is required",
    validate: (value: string) => {
      if (touchedFields?.[field.name] && !emailPattern.test(value)) {
        return "Error in email field";
      }
      if (values?.emailAddressValidation && value) {
        return value === values?.emailAddressValidation || "Emails don't match";
      }
      return true;
    },
  };

  const validateEmailConfirmation = {
    required: "Email is required",
    validate: (value: string) => {
      if (touchedFields?.[field.name] && !emailPattern.test(value)) {
        return "Error in email field";
      }
      if (!value) return true;
      return value === values?.emailAddress || "Emails don't match";
    },
  };

  const fieldValidation =
    dynamicValidation ??
    (field.name === "emailAddress"
      ? validateEmail
      : field.name === "emailAddressValidation"
        ? validateEmailConfirmation
        : field.validation);

  const isProtectedField = protectedFields.includes(field.name);

  const registerWithMask = useHookFormMask(register);

  useDefaultValueAs({ field: field, setValue: setValue, values: values });

  const preventEvent = (e: React.ClipboardEvent<Element>) => {
    e.preventDefault();
  };

  const preventContext = (e: React.MouseEvent<Element>) => {
    e.preventDefault();
  };

  return (
    <>
      <FormField
        id={inputID}
        name={field.name}
        label={t[field.label] || field.label}
        className={className}
        helperMessage={
          (field.helperMessage && t[field.helperMessage]) || (field.help && t[field.help])
        }
        errorMessage={shouldShowError ? t[errors[field.name]?.message as string] : undefined}
        disclaimerAfterField={field.disclaimer && t[field.disclaimer]}
        disclaimerAlert={field.disclaimerAlert && t[field.disclaimerAlert]}
        successNote={field.successNote && t[field.successNote]}
        tooltip={field.tooltip && t[field.tooltip]}
        validate={validate}
        validField={validField}>
        <div className="input__wrapper">
          <Tag
            {...baseProps}
            {...(field.mask
              ? {
                  ...registerWithMask(field.name, field.mask, {
                    ...fieldValidation,
                    placeholder: "",
                  }),
                }
              : { ...register(field.name, fieldValidation) })}
            maxLength={field.maxLength}
            placeholder={field.placeholder && (t[field.placeholder] || field.placeholder)}
            type={field.type}
            {...(isProtectedField && {
              onCopy: preventEvent,
              onPaste: preventEvent,
              onCut: preventEvent,
              onContextMenu: preventContext,
            })}
          />
          {field.rightIcon && inputIcons[field.rightIcon] && (
            <span className="input__icon">{React.createElement(inputIcons[field.rightIcon])}</span>
          )}
        </div>
      </FormField>
    </>
  );
};
